import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@shared/contexts/LanguageContext";
import { logVisitorActivity } from "@shared/lib/visitorTracking";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import CoverPlaceholder from "@visitor/components/CoverPlaceholder";
import { toast } from "sonner";
import PreferencesPanel from "@visitor/components/PreferencesPanel";
import VisitorHeader from "@visitor/components/VisitorHeader";
import ScanNextButton from "@visitor/components/ScanNextButton";

// F-B visitor Exhibition detail. Mobile-first. Mirrors ZoneDetail's
// shell + preferences pattern. Lists the published zones inside this
// exhibition (AND-composition gate — a draft zone is invisible even
// when its parent exhibition is published; per Q-OQ-A).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExhibitionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, fontSize, t } = useLanguage();
  const [exhibition, setExhibition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const lastLoggedKey = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchExhibition = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/exhibitions/${id}`);
        if (cancelled) return;
        setExhibition(res.data);
        const key = `${id}-${location.key}`;
        if (lastLoggedKey.current !== key) {
          lastLoggedKey.current = key;
          logVisitorActivity(
            "exhibition",
            res.data.id,
            res.data.code,
            res.data.titleTc || res.data.titleEn,
          );
        }
      } catch {
        if (!cancelled) {
          toast.error(
            t({
              en: "Exhibition not found or not available",
              tc: "找不到展覽",
              sc: "找不到展览",
            }),
          );
          navigate("/qr-instructions");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchExhibition();
    return () => {
      cancelled = true;
    };
  }, [id, location.key, navigate, t]);

  const fontClass =
    fontSize === "small"
      ? "text-sm"
      : fontSize === "large"
        ? "text-xl"
        : "text-base";

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-black text-white"
        data-testid="exhibition-detail-loading"
      >
        {t({ en: "Loading…", tc: "載入中…", sc: "加载中…" })}
      </div>
    );
  }

  if (!exhibition) return null;

  const exTitle =
    language === "en" && exhibition.titleEn
      ? exhibition.titleEn
      : language === "sc" && exhibition.titleSc
        ? exhibition.titleSc
        : exhibition.titleTc;
  const exDesc =
    language === "en" && exhibition.descriptionEn
      ? exhibition.descriptionEn
      : language === "sc" && exhibition.descriptionSc
        ? exhibition.descriptionSc
        : exhibition.descriptionTc;

  const zoneLabel = (z) =>
    language === "en" && z.titleEn
      ? z.titleEn
      : language === "sc" && z.titleSc
        ? z.titleSc
        : z.titleTc;

  return (
    <div
      className="min-h-screen flex flex-col bg-black text-white pb-28"
      data-testid="exhibition-detail-page"
    >
      {/* Top level: no parent link (F-AA) */}
      <VisitorHeader onMenu={() => setPreferencesOpen(true)} />

      <div className="flex-1 max-w-4xl w-full mx-auto px-4 py-6 space-y-6">
        {exhibition.coverImageId && (
          <img
            src={`${API}/assets/${exhibition.coverImageId}`}
            alt={exTitle}
            className="w-full rounded-xl object-cover max-h-72"
            data-testid="exhibition-cover-image"
          />
        )}
        <div>
          <h1
            className={`text-3xl font-heading font-bold mb-2`}
            data-testid="exhibition-title"
          >
            {exTitle}
          </h1>
          <p
            className={`text-white/70 ${fontClass}`}
            data-testid="exhibition-description"
          >
            {exDesc}
          </p>
        </div>

        <div className="space-y-2">
          <h2 className={`text-xl font-semibold ${fontClass}`}>
            {t({ en: "Zones", tc: "區域", sc: "区域" })}
          </h2>
          {exhibition.zones && exhibition.zones.length > 0 ? (
            <ul
              className="divide-y divide-white/10 border border-white/10 rounded-xl overflow-hidden"
              data-testid="exhibition-zones-list"
            >
              {exhibition.zones.map((z) => (
                <li
                  key={z.id}
                  onClick={() => navigate(`/zone/${z.id}`)}
                  className="px-4 py-4 flex items-center justify-between gap-3 hover:bg-white/5 cursor-pointer"
                  data-testid={`exhibition-zone-${z.id}`}
                >
                  <div className="flex items-center gap-3">
                    {z.coverImageId ? (
                      <img
                        src={`${API}/assets/${z.coverImageId}`}
                        alt={zoneLabel(z)}
                        className="w-12 h-12 rounded-md object-cover"
                      />
                    ) : (
                      <CoverPlaceholder
                        className="w-12 h-12 rounded-md"
                        iconClassName="w-6 h-6"
                        testId={`cover-placeholder-${z.id}`}
                      />
                    )}
                    <span className={fontClass}>{zoneLabel(z)}</span>
                  </div>
                  <ChevronRight
                    className="w-5 h-5 text-white/40 shrink-0"
                    aria-hidden="true"
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p
              className={`text-white/50 ${fontClass}`}
              data-testid="exhibition-empty-zones"
            >
              {t({
                en: "No published zones yet.",
                tc: "尚未發布任何區域。",
                sc: "尚未发布任何区域。",
              })}
            </p>
          )}
        </div>
      </div>

      {/* Bottom bar — Scan next (F-AA) */}
      <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-white/10 p-4">
        <div className="max-w-4xl mx-auto">
          <ScanNextButton t={t} />
        </div>
      </div>

      <PreferencesPanel
        isOpen={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </div>
  );
};

export default ExhibitionDetail;
